import { api } from '../lib/api.js';
import { el } from '../lib/ui.js';
import type { HeartPlace, HeartPlaces } from '../../shared/heart-places.js';

type UI = {
  screen: (...nodes: Array<Node | string | null | undefined>) => void;
  testata: (title: string) => HTMLElement;
  onLeave: (cleanup: () => void) => void;
};

export async function renderHeartPlaces(ui: UI, params: URLSearchParams): Promise<void> {
  const gift = await api.get<HeartPlaces>('/api/luoghi-del-cuore');
  if (!gift.enabled || !gift.places.length) {
    ui.screen(ui.testata('Il tuo quaderno'), el('p', {}, 'Questa pagina non è disponibile. Le tue storie ti aspettano nella Casa.'), el('a', { href: '#home', class: 'btn-secondario' }, 'Torna alla Casa'));
    return;
  }
  let closePhoto = (): void => {};
  ui.onLeave(() => closePhoto());
  const detail = el('section', { class: 'heart-place-detail', id: 'heart-place-detail', 'aria-labelledby': 'heart-place-title', tabindex: '-1' });
  const choices = el('nav', { class: 'heart-place-choices', 'aria-label': 'Scegli un luogo' });
  const buttons = new Map<string, HTMLButtonElement>();
  const select = (place: HeartPlace, focus = true): void => {
    closePhoto();
    for (const [id, button] of buttons) button.setAttribute('aria-pressed', String(id === place.id));
    if (focus) history.replaceState(history.state, '', `#luoghi?luogo=${encodeURIComponent(place.id)}`);
    const pictures = el('div', { class: 'heart-place-photos' });
    for (const photo of place.photos) {
      const trigger = el('button', { type: 'button', class: 'heart-place-photo', 'aria-label': `Apri la foto: ${photo.caption}`, click: () => {
        const previousOverflow = document.body.style.overflow;
        const dialog = el('dialog', { class: 'heart-photo-dialog', 'aria-label': photo.caption },
          el('button', { class: 'btn-secondario', type: 'button', click: () => dialog.close() }, 'Chiudi la foto'),
          el('img', { src: `/api/media/${photo.id}?w=1600`, alt: photo.caption }),
          el('p', {}, photo.caption),
          el('a', { class: 'btn-secondario', href: `#storia/${photo.storyId}` }, 'Apri la sua storia'));
        dialog.addEventListener('close', () => { dialog.remove(); document.body.style.overflow = previousOverflow; if (trigger.isConnected) trigger.focus({ preventScroll: true }); closePhoto = () => {}; });
        document.body.append(dialog); document.body.style.overflow = 'hidden'; dialog.showModal();
        closePhoto = () => dialog.close();
      } },
        el('img', { src: `/api/media/${photo.id}?w=480`, alt: '', loading: 'lazy', width: '480', height: '320' }),
        el('span', {}, photo.caption));
      pictures.append(trigger);
    }
    detail.replaceChildren(
      el('p', { class: 'heart-eyebrow' }, 'UN LUOGO, LE TUE STORIE'),
      el('h2', { id: 'heart-place-title' }, place.title), el('p', { class: 'heart-place-description' }, place.description),
      pictures,
      el('div', { class: 'heart-place-stories' }, el('h3', {}, 'Le storie da ritrovare'),
        ...place.stories.map((s) => el('a', { href: `#storia/${s.id}`, class: 'heart-story-link' }, el('span', {}, s.title), el('span', { 'aria-hidden': 'true' }, '→')))),
    );
    if (focus) { detail.focus({ preventScroll: true }); detail.scrollIntoView({ block: 'start' }); }
  };
  for (const place of gift.places) {
    const button = el('button', { type: 'button', 'aria-pressed': 'false', 'aria-controls': 'heart-place-detail', click: () => select(place) },
      el('span', { class: 'heart-place-symbol', 'aria-hidden': 'true' }, place.symbol), el('span', {}, place.title));
    buttons.set(place.id, button); choices.append(button);
  }
  select(gift.places.find((p) => p.id === params.get('luogo')) ?? gift.places[0]!, false);
  ui.screen(ui.testata(gift.title),
    el('div', { class: 'heart-places-page' },
      el('header', { class: 'heart-places-hero' },
        el('p', { class: 'heart-eyebrow' }, `UN PICCOLO VIAGGIO, DA ${gift.sender.toLocaleUpperCase('it')}`),
        el('p', { class: 'heart-dedication' }, gift.dedication),
        el('img', { class: 'heart-panorama', src: '/illustrations/luoghi-del-cuore.webp', alt: '', width: '2048', height: '688', 'aria-hidden': 'true' }),
        el('p', { class: 'heart-art-note' }, 'Un acquerello ispirato ai tuoi racconti. Le fotografie qui sotto sono quelle del tuo quaderno.'),
        el('p', { class: 'heart-signature' }, 'Con tutto il mio bene, ', el('span', {}, gift.sender))),
      el('h2', { class: 'heart-choose-title' }, 'Dove ti va di tornare?'),
      el('p', { class: 'heart-instruction' }, 'Tocca un luogo per ritrovare le sue fotografie e le tue storie.'),
      choices, detail,
      el('a', { class: 'btn-quieto', href: '#home' }, 'Torno alla Casa')));
}
